import toast from "react-hot-toast";
import {
  ProgramTarget,
  ProgramType,
  prepareProgramFormData
} from "../../api/endpoints/programs/parsePrograms";
import { RewardTiers } from "../../../components/editor/inputs/programs/ProgramRewardsInput";
import { useNavigate } from "react-router-dom";
import {
  useDeleteProgramMutation,
  useGetProgramQuery,
  usePostProgramActivationMutation,
  useSetPrivateAccessUsersMutation,
  useUpdateProgramMutation,
  useUpdateProgramV2Mutation
} from "../../api/endpoints/programsApi";

import type { LinkedAssetInput } from "../../api/endpoints/programs/programFullview";

export type ProgramUpdateValues = {
  title: string;
  description: string;
  scope: string;
  outOfScope: string;
  targets: ProgramTarget[];
  // Optional list of Asset IDs selected from inventory to define scope (legacy)
  assetIds?: number[];
  // Enhanced: linked assets with target/resource selection
  linkedAssets?: LinkedAssetInput[];
  type: ProgramType;
  rewards?: RewardTiers;
  knownVulnerabilities?: string;
  other?: string;
  vpn?: string;
  termsOfService: string;
  credentials?: string;
  rewardPolicy: string;
  private?: boolean;
  profilePicture?: Blob[];
  attachments?: Blob[];
  testingType?: string;
  environmentType?: string;
  complianceType?: string;
  otherComplianceType?: string;
  expectedStartDate?: Date;
  expectedEndDate?: Date;
  notification_methods?: String[];
  existingAttachments?: string[];
  jira_url?: string;
  jira_email?: string;
  jira_api_token?: string;
  jira_project_key?: string;
  slack_channel_link?: string;
  owner_id?: number;
  company_id?: number;
};

/**
 * Provides access to a single program
 */
const useProgram = (id?: number) => {
  const navigate = useNavigate();
  const {
    data: program,
    isError,
    isLoading,
    refetch: refetchProgram
  } = useGetProgramQuery(id as number, {
    skip: id === undefined || id === null || isNaN(id)
  });
  const [updateProgramMutation] = useUpdateProgramMutation();
  const [updateProgramV2Mutation] = useUpdateProgramV2Mutation();
  const [setPrivateAccessUsersMutation] = useSetPrivateAccessUsersMutation();
  const [postProgramActivationMutataion] = usePostProgramActivationMutation();
  const [deleteProgramMutation] = useDeleteProgramMutation();

  /**
   * Provides error & success messages when updating private access users
   */
  const setPrivateAccessUsers = (users: number[]) => {
    if (id) {
      const loader = toast.loading("Updating private access users...");

      setPrivateAccessUsersMutation({ id, users }).then(res => {
        toast.remove(loader);

        if ("error" in res) {
          toast.error("Failed to update private access users...");
        } else {
          toast.success("Updated private access users!");
        }
      });
    } else {
      toast.error("Failed to update private access users...");
    }
  };

  /**
   * Adds the given user to the list of private access users for this program
   */
  const addPrivateAccessUser = (userId: number) => {
    if (id && program)
      setPrivateAccessUsers([
        ...(program.privateAccessUsers?.filter(id => id !== userId) || []),
        userId
      ]);
  };

  /**
   * Removes the given user from the list of private access users for this program
   */
  const removePrivateAccessUser = (userId: number) => {
    if (id && program)
      setPrivateAccessUsers(
        (program.privateAccessUsers || []).filter(id => id !== userId)
      );
  };

  /**
   * Makes a request to save the program in the backend
   * (creating it if it does not exist)
   */
  const saveProgram = (
    data: Omit<ProgramUpdateValues, "private"> & {
      private?: 0 | 1;
      existingAttachments?: string[];
      vaultConfigs?: any[];
    },
    options?: {
      retryCount?: number;
      preserveFormState?: boolean;
      onError?: (error: any) => void;
      onSuccess?: (programId: number) => void;
      architectureVersion?: number;
      programId?: number; // Allow overriding ID for draft saves
      skipNavigation?: boolean; // Whether to stay on the page after saving
      silentToast?: boolean; // Whether to suppress saving/success toasts (e.g. background autosave)
    }
  ) => {
    const { retryCount = 0, preserveFormState = true, onError, onSuccess, skipNavigation = false, silentToast = false } = options || {};
    const loader = !silentToast ? toast.loading(retryCount > 0 ? `Retrying... (${retryCount}/3)` : "Saving...") : null;

    // Extract file attachments from the data
    const attachments = Array.isArray(data.attachments)
      ? data.attachments.filter(item => item instanceof File)
      : [];

    const details = {
      ...data,
      payoutRange: [
        data.rewards?.low || 0,
        data.rewards?.medium || 0,
        data.rewards?.high || 0,
        data.rewards?.critical || 0
      ],
      private: data.private === 1 ? true : data.private === 0 ? false : false,
      triaged: id ? undefined : true,
      profilePicture: undefined,
      attachments: undefined,
      rewards: undefined,
      notification_methods: data.notification_methods,
      jira_url: data.jira_url,
      jira_email: data.jira_email,
      jira_api_token: data.jira_api_token,
      jira_project_key: data.jira_project_key,
      existingAttachments: data.existingAttachments || [],
      vaultConfigs: data.vaultConfigs
    };

    const profilePicture =
      data.profilePicture && data.profilePicture.length > 0
        ? data.profilePicture[0]
        : undefined;

    // Validation checks
    if (details.type === ProgramType.VDP && details.private) {
      toast.error("VDP cannot be private");
      if (loader) toast.remove(loader);
      return;
    }
    if (details.type === ProgramType.PTAAS && details.private === false) {
      toast.error("PTAAS cannot be public");
      if (loader) toast.remove(loader);
      return;
    }

    // Use V2 mutation if requested
    const mutationToUse = options?.architectureVersion === 2 ? updateProgramV2Mutation : updateProgramMutation;

    const targetId = options?.programId || id;
    mutationToUse({
      id: targetId,
      data: prepareProgramFormData({
        details,
        icon: profilePicture,
        attachments: attachments
      })
    }).then(res => {
      if (loader) toast.remove(loader);
      if ("error" in res) {
        let errorMessage = "Failed to save program";
        const errorData = (res as any)?.error?.data;

        if (typeof errorData === 'object' && errorData?.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if ((res as any)?.error?.message) {
          errorMessage = (res as any)?.error?.message;
        } else if (typeof (res as any)?.error === 'string') {
          errorMessage = (res as any)?.error;
        }

        // Retry logic for network errors
        if (retryCount < 3 && ((res as any)?.error?.status === 500 || (res as any)?.error?.status === 503)) {
          setTimeout(() => {
            saveProgram(data, { ...options, retryCount: retryCount + 1 });
          }, Math.pow(2, retryCount) * 1000); // Exponential backoff
          return;
        }

        toast.error(errorMessage);

        // Preserve form state on error if requested
        if (preserveFormState) {
          try {
            localStorage.setItem(`program-draft-${id || 'new'}`, JSON.stringify({
              data,
              timestamp: Date.now(),
              error: errorMessage
            }));
          } catch (e) {
            console.warn("Failed to save form state to localStorage:", e);
          }
        }

        // Call error callback if provided
        if (onError) {
          onError(res);
        }
      } else {
        if (!silentToast) toast.success("Program saved");

        // Clear any saved draft on successful save
        try {
          localStorage.removeItem(`program-draft-${id || 'new'}`);
          localStorage.removeItem(`program-autosave-${id || 'new'}`);
          localStorage.removeItem(`program-recovery-${id || 'new'}`);
        } catch (e) {
          console.warn("Failed to clear draft from localStorage:", e);
        }

        // Get the program ID (either new or existing)
        const programId = res?.data || id;

        // Call success callback with the program ID if provided
        if (onSuccess && programId) {
          onSuccess(programId);
        }

        if (!skipNavigation) {
          if (res?.data) {
            navigate(`/dashboard/programs/${(res as { data: number }).data}`);
          } else if (id) {
            navigate(`/dashboard/programs/${id}`);
          } else {
            navigate(`/dashboard/programs`);
          }
        }
      }
    }).catch(error => {
      if (loader) toast.remove(loader);
      console.error("Program save error:", error);

      // Retry logic for network errors
      if (retryCount < 3) {
        setTimeout(() => {
          saveProgram(data, { ...options, retryCount: retryCount + 1 });
        }, Math.pow(2, retryCount) * 1000); // Exponential backoff
        return;
      }

      const errorMessage = error?.message || "Network error occurred while saving";
      toast.error(errorMessage);

      // Preserve form state on error
      if (preserveFormState) {
        try {
          localStorage.setItem(`program-draft-${id || 'new'}`, JSON.stringify({
            data,
            timestamp: Date.now(),
            error: errorMessage
          }));
        } catch (e) {
          console.warn("Failed to save form state to localStorage:", e);
        }
      }

      // Call error callback if provided
      if (onError) {
        onError(error);
      }
    });
  };

  return {
    program,
    isLoading,
    isError,
    addPrivateAccessUser,
    removePrivateAccessUser,
    saveProgram,
    toggleProgramActivation: (isActivated: boolean) => {
      if (id) {
        const loader = toast.loading("Processing...");

        postProgramActivationMutataion({ id, isActivated }).then(res => {
          toast.remove(loader);

          if ("error" in res) {
            toast.error(
              `Failed to ${isActivated ? "activate" : "deactivate"} program`
            );
          } else {
            toast.success(
              `Program ${isActivated ? "activated!" : "deactivated."}`
            );
          }
        });
      }
    },
    deleteProgram: () => {
      if (id) {
        const loader = toast.loading("Deleting...");

        deleteProgramMutation(id).then(res => {
          toast.remove(loader);

          if ("error" in res) {
            toast.error("Failed to delete program...");
          } else {
            toast.success("Program deleted!");
          }
        });
      }
    },
    refetch: refetchProgram
  };
};

export default useProgram;
